import { ApplicationRef, ComponentRef, createComponent, EnvironmentInjector, inject, Injectable } from '@angular/core';
import { GlobalSettingsService } from './global-settings.service';
import { IllumLiveService } from './illum-live.service';
import {
  BehaviorSubject,
  catchError,
  finalize,
  firstValueFrom,
  from,
  interval,
  Subscription,
  switchMap,
  takeWhile,
  timer,
} from 'rxjs';
import { ArcAngelSharedService } from './arc-angel-shared.service';
import { ArcAngelService } from './arc-angel.service';
import { DataContextService } from './data-context.service';
import { CoresService } from './cores.service';
import { CoreService } from './core.service';
import { WindowService } from './window.service';
import { ArcAngelMonitorModeService } from '../../components/shared/arc-angel-monitor-mode.service';
import { IncidentVisualizerComponent } from '../../components/shared/incident-visualizer/incident-visualizer.component';
import { FloorService } from './floor.service';
import * as Cesium from 'cesium';
import { GlobeService } from './globe.service';
import { TimelineService } from './timeline.service';
import { DeviceService } from './device.service';
import { OrganizationUtilService } from './organization-util.service';

@Injectable({
  providedIn: 'root',
})
export class ArcAngelLiveModeService {
  // Data properties
  data: any = {
    liveIncident: undefined as any,
    liveProcall: undefined as any,
    liveCalendar: undefined as any,
    liveTracking: undefined as any,
    calls: 0,
    userStatusByIncidentDict: undefined as any // here we will store the all active incident data
  };

  private liveEventsProcallsPromise: any;
  private liveEntitiesAdded = {
    updates: {} as Record<string, any>,
    safezones: {} as Record<string, any>,
  };
  private liveModeAvailable = false;
  private currentTrackingOrProcallId: string | undefined;
  private currentProfileIdForTrackingOrProcall: string | undefined;
  private lastTrackingOrProcall: string | undefined;
  private lastEventInfo: any;
  private firstPollerCalled = false;
  private liveEventId: string | undefined;
  private liveIncidentPollerRemoved = false;
  private core = inject(CoresService);
  private globalSettings = inject(GlobalSettingsService);
  private deviceService = inject(DeviceService);
  private organizationutilService = inject(OrganizationUtilService);
  private illumLiveService = inject(IllumLiveService);
  private arcAngelSharedService = inject(ArcAngelSharedService);
  private arcAngelService = inject(ArcAngelService);
  private dataContext = inject(DataContextService);
  private windowService = inject(WindowService);
  private liveIncidentPollerSubscription?: Subscription;
  private cor = inject(CoreService);
  private incidentVisualizerRef: ComponentRef<IncidentVisualizerComponent> | null = null;
  private incidentVisualizerCloseSub: Subscription | null = null;
  private currentLiveIncidentStatusSubject = new BehaviorSubject<boolean>(false);
  public currentLiveIncidentStatus$ = this.currentLiveIncidentStatusSubject.asObservable();

  private monitorModeService = inject(ArcAngelMonitorModeService);
  //private sessionOptionsService = inject(SessionOptionsService);
   constructor(
      private appRef: ApplicationRef,
      private environmentInjector: EnvironmentInjector,
      private floorService: FloorService,
      private globeService: GlobeService,
      private timelineService: TimelineService
    ) {}
  async startLiveIncidentMode(eventId: string, scope: any): Promise<void> {
    this.timelineService.setTimelineByMode("Live Mode");
    this.liveEventId = eventId;
    this.monitorModeService.clearMonitorUpdates();

    this.windowService.closeAllMapEntityDetailsWindow();
    // Remove any existing live incident and its entities before starting
    this.clearLiveMode();

    this.setupLiveMode();
    // reset drawer height to full
    // document.getElementById('sliding-drawer-plugin-panel')?.style.setProperty('bottom', '0px');
    this.liveIncidentPollerRemoved = false;
    this.firstPollerCalled = true;
    try {
      await this.liveIncidentPoller().then(() => {
        if (this.data.liveIncident) {
          this.data.liveIncident.show();
          // this.core.setSceneModeTo3d();
          // this.cor.setSceneModeTo3d();
          // const viewer = this.core.getViewer();
          // const v = this.cor.getViewer();
          // viewer.camera.switchToOrthographicFrustum();
          // v.camera.switchToOrthographicFrustum();
          // if (viewer && viewer.camera) {
          //   viewer.camera.switchToOrthographicFrustum();
          // }
          // if (!this.data.liveIncident.isBuildingIncident) {
          //   this.data.liveIncident.goToEntity();
          // }
          setTimeout(() => {
            if (!this.data.liveIncident.isBuildingIncident()) {
              if (this.data.liveIncident.locations) {
                const rectangle = this.getRectangleFromLocations(this.data.liveIncident.locations);
                this.goToAdHocRegion(rectangle);
              }
              this.openIncidentVisualizer(this.data.liveIncident, this.data.liveIncident);
            } else {
              this.data.liveIncident.viewDetailsWindow(scope);
            }
          }, 10);
        }
        return;
      });
    } catch (error) {
      console.error('Error starting live incident poller.', error);
      throw error;
    } finally {
      this.kickOffLiveIncidentPoller();
    }
  }

  // Example: stopLiveMode
  stopLiveMode(): void {
    // clearTimeout(this.liveEventsProcallsPromise);
    // this.pollerService.remove('liveIncidentPoller');
    this.closeIncidentVisualizer();
    this.liveIncidentPollerRemoved = true;
    // this.userService.cancelProcessingUserTimeout();
    // this.globalSettings.setIsProcessingUser(false);
    // this.globalSettings.setLiveIncidentPollerIsRunning(false);
    this.clearLiveMode();
  }

  setupLiveMode(): void {
    this.clearLiveMode();
    this.liveModeAvailable = true;
  }

  clearLiveMode(): void {
    if (this.data.liveIncident) {
      // this.userService.removeDevicesForIncident(this.data.liveIncident);
      this.dataContext.removeMapEntity(this.data.liveIncident);
      this.data.liveIncident = undefined;
    }
    if (this.data.liveProcall) {
      // this.userService.removeProcallDevice(this.data.liveProcall);
      this.data.liveProcall = undefined;
    }
    if (this.data.liveCalendar) {
      // this.userService.removeCalendarEvent(this.data.liveCalendar);
      this.data.liveCalendar = undefined;
    }
    if (this.data.liveTracking) {
      // this.userService.removeActiveTrackingEvent(this.data.liveTracking);
      this.data.liveTracking = undefined;
    }
    for (const updateId in this.liveEntitiesAdded.updates) {
      const update = this.liveEntitiesAdded.updates[updateId];
      this.dataContext.removeMapEntity(update);
    }
    this.liveEntitiesAdded.updates = {};
    for (const safezoneId in this.liveEntitiesAdded.safezones) {
      const safezone = this.liveEntitiesAdded.safezones[safezoneId];
      this.dataContext.removeMapEntity(safezone);
    }
    this.liveEntitiesAdded.safezones = {};
    // Remove user cluster popup and/or user search popup
    // document.getElementById('userclusterlistuidiv')?.remove();
    // document.getElementById('usersearchuidiv')?.remove();
  }

  // ...continue converting each method in the same way

  // Example: liveIncidentPoller

  parseLiveEvent(response: any, scope?: any): void {
    const incident = response.data.items[0];
    // If incident is resolved then go to monitor mode
    if (incident?.status === 'inactive' || incident?.status === 'resolved') {
      this.clearLiveMode();
      this.stopLiveMode();
      this.currentLiveIncidentStatusSubject.next(true);
      return;
    }
    // incident
    if (!this.data.liveIncident) {
      const incidentEntity = this.arcAngelSharedService.parseIncident(
        incident,
        incident.id,
        false
      );
      if (!incidentEntity) {
        return;
      }
      incidentEntity.incidentPerimeter = incident.incidentPerimeter;
      this.data.liveIncident = incidentEntity;
      this.dataContext.addMapEntity(this.data.liveIncident);
      this.arcAngelService.setBuildingIncident(incidentEntity);
    } else {
      this.arcAngelSharedService.updateIncident(
        incident,
        this.data.liveIncident
      );
    }
    
    // Check if it's a campus incident or building incident by checking affectedPerimeterIds against campus IDs
    const campuses = this.organizationutilService.data?.myCampus?.campuses || [];
    const campusIds = new Set(campuses.map((c: any) => String(c.campusId)));
    const incidentPerimeterIds = incident.affectedPerimeters?.map((p: any) => String(p.perimeterId)) || [];
    const isCampusIncident = incidentPerimeterIds.some((perimId: string) => campusIds.has(perimId));
    
    if (isCampusIncident) {
      // For campus incidents, use campus device count
      this.deviceService.getCountCampusDevicesByPerimeterAndStatus(+incident.id).subscribe(() => {
        // Handle the campus incident count
      });
    } else {
      // For building incidents, use building device count
      this.deviceService.getCountDevicesByPerimeterAndStatus(+incident.id).subscribe(() => {
        // Handle the building incident count
      });
    }

    if (
      incident.affectedPerimeters !== undefined &&
      incident.affectedPerimeters.length > 0
    ) {
      if (typeof this.data.liveIncident.setAffectedPerimeters === 'function') {
        this.data.liveIncident.setAffectedPerimeters(
          this.arcAngelService.getAffectedPerimeterEntities(
            incident.affectedPerimeters
          )
        );
      }
    }
    const updateIds: Record<string, boolean> = {};
    for (const updateItem of incident.updates) {
      const update = updateItem;
      updateIds[update.id] = true;

      // Remove existing entity with this ID before adding
      const existing = this.dataContext.getMapEntity(update.id);
      if (existing) {
        this.dataContext.removeMapEntity(existing, true);
      }

      let updateEntity;
      if (!(update.id in this.liveEntitiesAdded.updates)) {
        updateEntity = this.arcAngelSharedService.parseIncidentUpdate(
          update,
          incident,
          false
        );
        if (updateEntity) {
          this.liveEntitiesAdded.updates[update.id] = updateEntity;
          if (this.data.liveIncident.removeUpdate)
            this.data.liveIncident.removeUpdate(updateEntity);
          if (this.data.liveIncident.addUpdate)
            this.data.liveIncident.addUpdate(updateEntity);
          this.addIncidentUpdateToBuildingIncident(updateEntity);

          // Add to Cesium map
          // this.dataContext.addMapEntity(updateEntity);
        }
      } else {
        updateEntity = this.liveEntitiesAdded.updates[update.id];
        this.arcAngelSharedService.updateIncidentUpdate(update, updateEntity);
      }
      if (updateEntity.data?.status === 'PENDING' && !this.windowService.windowIsOpen('window-' + updateEntity.id)) {
        if (typeof updateEntity.viewDetailsWindow === 'function') {
          updateEntity.viewDetailsWindow(scope);
        }
      }
    }
    // safe zones
    const safezoneIds: Record<string, boolean> = {};
    let safezone;
    for (const safezoneItem of incident.safezones) {
      safezone = safezoneItem;
      const isSzActive = safezone.isActive !== false && safezone.isActive !== 'false' && safezone.active !== false && safezone.active !== 'false';
      if (!isSzActive) {
        if (safezone.id in this.liveEntitiesAdded.safezones) {
          const existingEntity = this.liveEntitiesAdded.safezones[safezone.id];
          delete this.liveEntitiesAdded.safezones[safezone.id];
          if (this.data.liveIncident.removeSafeZone) {
            this.data.liveIncident.removeSafeZone(existingEntity);
          }
          this.dataContext.removeMapEntity(existingEntity, true);
        }
        continue;
      }
      safezoneIds[safezone.id] = true;
      if (!(safezone.id in this.liveEntitiesAdded.safezones)) {
        // Remove existing entity with this ID from the map, if present
        const existing = this.dataContext.getMapEntity(safezone.id);
        if (existing) {
          this.dataContext.removeMapEntity(existing, true);
        }
        const safeZoneEntity = this.arcAngelSharedService.parseIncidentSafezone(
          safezone,
          incident,
          false
        );
        if (safeZoneEntity) {
          this.liveEntitiesAdded.safezones[safezone.id] = safeZoneEntity;
          if (this.data.liveIncident.addSafeZone)
            this.data.liveIncident.addSafeZone(safeZoneEntity);
        }
      } else {
        this.arcAngelSharedService.updateIncidentSafezone(
          safezone,
          this.liveEntitiesAdded.safezones[safezone.id]
        );
      }
    }
    // Remove deleted safezones and updates.
    if (
      this.data.liveIncident.getSafeZones &&
      this.data.liveIncident.removeSafeZone
    ) {
      const safezonesArray = this.data.liveIncident.getSafeZones().slice();
      for (const safezoneItem of safezonesArray) {
        safezone = safezoneItem;
        if (!(safezone.getArcAngelId() in safezoneIds)) {
          this.data.liveIncident.removeSafeZone(safezone);
          this.dataContext.removeMapEntity(safezone, true);
        }
      }
    }
    if (
      this.data.liveIncident.getUpdates &&
      this.data.liveIncident.removeUpdate
    ) {
      const updatesArray = this.data.liveIncident.getUpdates().slice();
      for (const updateItem of updatesArray) {
        const update = updateItem;
        if (!(update.getArcAngelId() in updateIds)) {
          this.data.liveIncident.removeUpdate(update);
          this.dataContext.removeMapEntity(update, true);
        }
      }
    }
    // set incident polygon height to above ground
    const incidentHeight = this.data.liveIncident.incidentPerimeter
      ? this.data.liveIncident.incidentPerimeter.height
      : 1;
    if (
      this.data.liveIncident &&
      this.data.liveIncident.cesiumEntity &&
      this.data.liveIncident.cesiumEntity.polygon
    ) {
      this.data.liveIncident.cesiumEntity.polygon.height = incidentHeight;
    }
    if (
      this.data.liveIncident.outline &&
      typeof this.data.liveIncident.outline.getAltitude === 'function' &&
      typeof this.data.liveIncident.outline.setAltitude === 'function'
    ) {
      if (this.data.liveIncident.outline.getAltitude() !== incidentHeight) {
        this.data.liveIncident.outline.setAltitude(incidentHeight);
      }
    }
    this.data.calls++;
    return;
  }
  public resetCurrentLiveIncidentStatus(): void {
    this.currentLiveIncidentStatusSubject.next(false);
  }
  async liveIncidentPoller(): Promise<any> {
    if (
      this.globalSettings.liveIncidentPollerIsRunning ||
      !this.firstPollerCalled ||
      this.liveIncidentPollerRemoved
    ) {
      return Promise.resolve(); // same as $q.resolve()
    }
    this.globalSettings.setLiveIncidentPollerIsRunning(true);
    // eslint-disable-next-line no-useless-catch
    try {
      const response = await firstValueFrom(
        this.illumLiveService.getIncident(this.liveEventId ?? '0').pipe(
          catchError((error) => {
            this.globalSettings.setIsProcessingUser(false);
            this.globalSettings.setCurrentEventsProcallsPollerIsRunning(false);
            this.globalSettings.setLiveIncidentPollerIsRunning(false);
            console.error('Error parsing live event.', error);
            throw error;
          }),
          finalize(() => {
            this.globalSettings.setLiveIncidentPollerIsRunning(false);
          })
        )
      );
      return this.parseLiveEvent(response);
    } catch (error) {
      throw error;
    }
  }

  private addIncidentUpdateToBuildingIncident(updateEntity: any): void {
    if (
      !this.arcAngelService.data ||
      !Array.isArray(this.arcAngelService.data.myBuildings)
    ) {
      return;
    }
    for (const building of this.arcAngelService.data.myBuildings) {
      if (!Array.isArray(building.incidents)) continue;
      for (const incident of building.incidents) {
        if (incident.eventId === updateEntity.eventId) {
          // Remove if exists
          const index = incident.updates.findIndex(
            (update: any) => update.data.id === updateEntity.data.id
          );
          if (index !== -1) {
            incident.updates.splice(index, 1);
          }
          incident.updates.push(updateEntity);
        }
      }
    }
  }

  // Add setBuildingIncident method
  setBuildingIncident(incident: any): void {
    if (
      !this.arcAngelService.data ||
      !Array.isArray(this.arcAngelService.data.myBuildings)
    ) {
      return;
    }
    for (const building of this.arcAngelService.data.myBuildings) {
      if (
        typeof building.isIncidentABuildingIncident === 'function' &&
        building.isIncidentABuildingIncident(incident)
      ) {
        if (typeof building.addIncident === 'function') {
          building.addIncident(incident);
        }
      }
    }
  }

  // ...continue for all other methods

  // Utility: kick off poller
  kickOffLiveIncidentPoller(): void {
    if (this.liveIncidentPollerSubscription) {
      this.liveIncidentPollerSubscription.unsubscribe();
    }
    this.liveIncidentPollerSubscription = interval(4000).pipe(
      takeWhile(() => !this.liveIncidentPollerRemoved),
      switchMap(() => from(this.liveIncidentPoller()))
    ).subscribe({
      next: () => {
        if (this.data.liveIncident) {
        }
      },
      error: (error) => {
        console.error('Live incident polling failed:', error);
      }
    });
  }

  stopLiveIncidentPoller(): void {
    if (this.liveIncidentPollerSubscription) {
      this.liveIncidentPollerSubscription.unsubscribe();
      this.liveIncidentPollerSubscription = undefined;
    }
  }
    private openIncidentVisualizer(incident: any, arcLiveEntity?: any): void {
    try {
      this.closeIncidentVisualizer();
      this.incidentVisualizerRef = createComponent(IncidentVisualizerComponent, {
        environmentInjector: this.environmentInjector
      });
      this.incidentVisualizerRef.instance.incident = incident;
      this.incidentVisualizerRef.instance.isPlaybackMode = true;
      if (arcLiveEntity) {
        this.incidentVisualizerRef.instance.mapentityid = arcLiveEntity;
      }
      this.incidentVisualizerCloseSub = this.incidentVisualizerRef.instance.closeRequested.subscribe(() => {
        this.closeIncidentVisualizer();
      });

      this.incidentVisualizerRef.changeDetectorRef.detectChanges();
      document.body.appendChild(this.incidentVisualizerRef.location.nativeElement);
      this.floorService.updateStreetViewData(arcLiveEntity);
      this.floorService.updateIncidentVisualizer(true);
      this.appRef.attachView(this.incidentVisualizerRef.hostView);

    } catch (error) {
      console.error('[openIncidentVisualizer] Error opening incident visualizer:', error);
    }
  }
  private closeIncidentVisualizer(): void {
    if (this.incidentVisualizerRef) {
      if (this.incidentVisualizerCloseSub && !this.incidentVisualizerCloseSub.closed) {
        this.incidentVisualizerCloseSub.unsubscribe();
      }
      this.incidentVisualizerCloseSub = null;
      this.appRef.detachView(this.incidentVisualizerRef.hostView);
      this.incidentVisualizerRef.destroy();
      this.incidentVisualizerRef = null;
      this.floorService.updateIncidentVisualizer(false);
    }
  }
  private getRectangleFromLocations(locations: any[]): any {
    if (!locations || locations.length === 0) {
      throw new Error('No locations provided for rectangle calculation.');
    }
    const lats: number[] = [];
    const lons: number[] = [];
    locations.forEach((loc: any) => {
      if (Array.isArray(loc) && loc.length >= 2 && isFinite(loc[0]) && isFinite(loc[1])) {
        lats.push(loc[0]);
        lons.push(loc[1]);
      }
    });
    if (!lats.length || !lons.length) {
      throw new Error('Invalid locations data for rectangle calculation.');
    }
    const buffer = 0.01;
    let west = Math.min(...lons) - buffer;
    let east = Math.max(...lons) + buffer;
    let north = Math.max(...lats) + buffer;
    let south = Math.min(...lats) - buffer;

    const clamp = (val: number, min: number, max: number) => Math.max(min, Math.min(max, val));
    west = clamp(west, -180, 180);
    east = clamp(east, -180, 180);
    north = clamp(north, -90, 90);
    south = clamp(south, -90, 90);


    return Cesium.Rectangle.fromDegrees(west, south, east, north);
  }
  private goToAdHocRegion(rectangle: any): void {
    const viewer = this.globeService.getViewer();

    if (
      !viewer ||
      !viewer.camera ||
      !rectangle ||
      !isFinite(rectangle.west) ||
      !isFinite(rectangle.south) ||
      !isFinite(rectangle.east) ||
      !isFinite(rectangle.north) ||
      rectangle.west === rectangle.east ||
      rectangle.south === rectangle.north ||
      (rectangle.east - rectangle.west) < 0.0001 ||
      (rectangle.north - rectangle.south) < 0.0001
    ) {
      console.error('Invalid rectangle or viewer for flyTo:', rectangle);
      return;
    }

    const destination = Cesium.Rectangle.fromDegrees(
      Cesium.Math.toDegrees(rectangle.west),
      Cesium.Math.toDegrees(rectangle.south),
      Cesium.Math.toDegrees(rectangle.east),
      Cesium.Math.toDegrees(rectangle.north)
    );

    try {
      if (viewer.scene.mode !== Cesium.SceneMode.SCENE3D) {
        viewer.scene.morphTo3D(0);
        viewer.scene.morphComplete.addEventListener(() => {
          viewer.camera.flyTo({ destination });
        });
      } else {
        setTimeout(() => {
          viewer.camera.flyTo({ destination });
        }, 100);
      }
    } catch (e) {
      console.error('Cesium flyTo error:', e, destination);
      try {
        viewer.camera.setView({ destination });
      } catch (e2) {
        console.error('Cesium setView error:', e2, destination);
      }
    }
  }
  public closeIncidentVisualizerFromOutside(): void {
    this.closeIncidentVisualizer();
  }
}
